import React, { useState, useEffect, useRef } from 'react';
import { Container, Button, Spinner, ProgressBar, Row, Col, Card } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';

function BatchUpload() {
    const navigate = useNavigate();
    const fileInputRef = useRef(null);
    const folderInputRef = useRef(null);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [previews, setPreviews] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [uploadComplete, setUploadComplete] = useState(false);
    const [selectedModel, setSelectedModel] = useState('gemini');
    const [error, setError] = useState(null);
    const [polling, setPolling] = useState(false);

    // Processing status
    const [status, setStatus] = useState({
        unprocessed: 0,
        processing: 0,
        completed: 0,
        failed: 0,
        total: 0
    });

    useEffect(() => {
        fetchStatus(); 
    }, []);

    useEffect(() => {
        let interval;
        if (polling) {
            fetchStatus(); // Fetch immediately when polling starts
            interval = setInterval(fetchStatus, 2000);
        }
        return () => clearInterval(interval);
    }, [polling]);

    const fetchStatus = async () => {
        try {
            const res = await fetch('/api/processing_status');
            if (res.ok) {
                const data = await res.json();
                console.log("Data: ", data);
                setStatus(data);
                
                // If there are no unprocessed or processing items left, we can stop polling
                if (data.unprocessed === 0 && data.processing === 0 && (data.completed+data.failed) === data.total) {
                    setPolling(false);
                }
                setPolling(data.unprocessed > 0 || data.processing > 0); // Continue polling if there are still items being processed
            }
        } catch (err) {
            console.error("Error fetching status:", err);
        }
    };

    const handleFileChange = (e) => {
        const files = Array.from(e.target.files);
        if (files.length > 0) {
            setSelectedFiles(files);
            
            // Create previews
            const newPreviews = files.map(file => URL.createObjectURL(file));
            setPreviews(newPreviews);
            setUploadComplete(false);
        }
    };

    const handleDrop = async (e) => {
        e.preventDefault();
        if (uploading || polling) return;
        
        // Extract entries synchronously because dataTransfer gets cleared after the first await
        const topLevelEntries = [];
        if (e.dataTransfer.items) {
            for (let i = 0; i < e.dataTransfer.items.length; i++) {
                const item = e.dataTransfer.items[i];
                if (item.kind === 'file') {
                    const entry = item.webkitGetAsEntry();
                    if (entry) {
                        topLevelEntries.push(entry);
                    }
                }
            }
        }
        const fallbackFiles = e.dataTransfer.files ? Array.from(e.dataTransfer.files) : [];
        
        const allFiles = [];

        const readEntry = async (entry) => {
            if (entry.isFile) {
                return new Promise((resolve) => {
                    entry.file(file => {
                        if (file.type.startsWith('image/')) {
                            allFiles.push(file);
                        }
                        resolve();
                    });
                });
            } else if (entry.isDirectory) {
                const dirReader = entry.createReader();
                let entries = [];
                const readEntries = async () => {
                    const results = await new Promise((resolve) => {
                        dirReader.readEntries(resolve);
                    });
                    if (results.length > 0) {
                        entries = entries.concat(results);
                        await readEntries();
                    }
                };
                await readEntries();
                for (let i = 0; i < entries.length; i++) {
                    await readEntry(entries[i]);
                }
            }
        };

        if (topLevelEntries.length > 0) {
            for (let i = 0; i < topLevelEntries.length; i++) {
                await readEntry(topLevelEntries[i]);
            }
        } else if (fallbackFiles.length > 0) {
            const files = fallbackFiles.filter(file => file.type.startsWith('image/'));
            allFiles.push(...files);
        }

        if (allFiles.length > 0) {
            setSelectedFiles(allFiles);
            const newPreviews = allFiles.map(file => URL.createObjectURL(file));
            setPreviews(newPreviews);
            setUploadComplete(false);
        }
    };

    const handleUpload = async () => {
        if (selectedFiles.length === 0) return;

        setError(null);
        setUploading(true);
        const formData = new FormData();
        
        selectedFiles.forEach(file => {
            formData.append('images', file);
        });
        formData.append('model', selectedModel);

        try {
            const response = await fetch('/api/upload_images_batch', {
                method: 'POST',
                body: formData,
            });

            if (!response.ok) {
                throw new Error('Failed to upload batch');
            }

            setUploadComplete(true);
            // Reset status so we don't show the old batch's progress
            setStatus({
                unprocessed: selectedFiles.length,
                processing: 0,
                completed: 0,
                failed: 0,
                total: selectedFiles.length
            });
            setPolling(true); // Start polling the background processing status
        } catch (err) {
            setError(err.message);
        } finally {
            setUploading(false);
        }
    };

    const totalPending = status.unprocessed
    const progressPercent = status.total > 0 ? (status.completed+status.failed) / status.total * 100 : 0;

    return (
        <Container className="py-4">
            <div className="d-flex justify-content-between align-items-center mb-4 justify-content-center">
                <h2 className="section-title mb-0 mx-auto">Upload Images</h2>
                {/* <Button variant="outline-secondary" className="rounded-pill px-4" onClick={() => navigate('/collection')}>
                    Back to Collection
                </Button> */}
            </div>

            <Card className="shadow-sm border-0 mb-4 p-4 rounded-4">
                <input 
                    type="file" 
                    accept="image/*" 
                    multiple 
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                />
                <input 
                    type="file" 
                    accept="image/*" 
                    multiple 
                    webkitdirectory="true"
                    ref={folderInputRef}
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                />
                <div 
                    className={`upload-dropzone p-5 text-center bg-light rounded-4 border-dashed mb-4 ${(uploading || polling) ? 'opacity-50' : ''}`} 
                    style={{ border: '2px dashed #ccc' }}
                    onDrop={handleDrop}
                    onDragOver={(e) => e.preventDefault()}
                >
                    <div className="text-muted">
                        <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>+</div>
                        <h4>Drag and drop images here</h4>
                        <p>JPG, PNG or WEBP</p>
                    </div>
                    <div className="d-flex justify-content-center gap-3 mt-4">
                        <Button 
                            // variant="outline-primary" 
                            className="btn-primary-custom rounded-pill px-4"
                            onClick={() => fileInputRef.current.click()}
                            disabled={uploading || polling}
                        >
                            Upload Files
                        </Button>
                        <Button 
                            // variant="outline-primary" 
                            className="btn-primary-custom rounded-pill px-4"
                            onClick={() => folderInputRef.current.click()}
                            disabled={uploading || polling}
                        >
                            Upload Folder
                        </Button>
                    </div>
                </div>

                <div className="d-flex justify-content-between align-items-center mb-4">
                    <div className="w-25">
                        <label className="form-label text-muted small fw-bold">Select Model</label>
                        <select 
                            className="form-select rounded-pill" 
                            value={selectedModel} 
                            onChange={(e) => setSelectedModel(e.target.value)}>
                            <option  value="gemini" className={`${selectedModel === 'gemini' ? 'd-none' : ''}`}>Gemini</option>
                            <option value="qwen" className={`${selectedModel === 'qwen' ? 'd-none' : ''}`}>Qwen</option>
                        </select>
                    </div>
                    
                    <Button 
                        className="btn-primary-custom rounded-pill px-5 py-2" 
                        disabled={selectedFiles.length === 0 || uploading || polling}
                        onClick={handleUpload}
                    >
                        {uploading ? (
                            <span><Spinner animation="border" size="sm" className="me-2" /> Uploading...</span>
                        ) : polling ? (
                            <span>Processing...</span>
                        ) : (
                            'Upload All'
                        )}
                    </Button>
                </div>

                {error && <div className="alert alert-danger rounded-pill">{error}</div>}

                {(polling || uploadComplete) && (
                    <div className="mb-4 p-4 bg-light rounded-4">
                        <h5 className="mb-3">Processing Status</h5>
                        <ProgressBar 
                            animated={totalPending > 0} 
                            now={progressPercent} 
                            label={`${Math.round(progressPercent)}%`} 
                            className="mb-3 rounded-pill" 
                            style={{ height: '25px' }}
                        />
                        <div className="d-flex justify-content-between text-muted small fw-bold">
                            <span>Unprocessed: {totalPending}</span>
                            <span>Processed: {status.completed}/{status.total}</span>
                            <span>In process: {status.processing}</span>
                            <span>Failed: {status.failed}</span>
                            <span>Total: {status.total}</span>
                        </div>
                        {status.total === (status.completed+status.failed) && (
                            <div className="alert alert-success mt-3 rounded-pill text-center mb-0">
                                All images processed successfully!
                            </div>
                        )}
                    </div>
                )}
            </Card>

            {previews.length > 0 && (
                <div>
                    <h4 className="mb-3">Selected Images ({previews.length})</h4>
                    <Row xs={2} sm={3} md={4} lg={5} className="g-3">
                        {previews.map((src, index) => (
                            <Col key={index}>
                                <Card className="border-0 shadow-sm h-100 rounded-3 overflow-hidden">
                                    <div style={{ height: '120px' }}>
                                        <Card.Img 
                                            variant="top" 
                                            src={src} 
                                            style={{ width: '100%', height: '100%', objectFit: 'contain' }} 
                                        />
                                    </div>
                                    <Card.Body className="p-2 text-center bg-light">
                                        <small className="text-muted text-truncate d-block">
                                            {selectedFiles[index].name}
                                        </small>
                                    </Card.Body>
                                </Card>
                            </Col>
                        ))}
                    </Row>
                </div>
            )}
        </Container>
    );
}

export default BatchUpload;
